import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {ParticleState} from '@shared/store/reducers/particle.reduer';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {QueryProvider} from '@shared/queries/query-provider';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import { MatRadioChange } from '@angular/material/radio';
import {Configuration} from "@shared/models/configuration";
import {CaseType} from "../../Fields";
import {BasketProvider} from "@shared/models/basket-provider";
import {skip, takeUntil} from "rxjs/operators";

@Component({
    standalone: false,
    selector: 'particle-copy-case-dialog',
  templateUrl: './copy-case-dialog.component.html',
  styleUrls: ['./copy-case-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CopyCaseDialogComponent implements OnInit, IDialogView, OnDestroy {
  data: DialogModel;
  form: FormGroup;
  inputForm: any;
  inputFormError: any;
  unsubscribe$ = new Subject<void>();
  stages: any;
  selectedStage: any;
  selectedStageId: number;
  filedPeriodList$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  inputFormErrors: any;
  availableStages = [];
  caseContentLinkItems: any;
  filingModels: any;
  isCaseTypeDisabled: boolean;
  isReadOnly: boolean;
  isArchived : boolean;

  constructor (private formBuilder: FormBuilder,
               protected particleStore: Store<ParticleState>,
               protected queryStore: Store<IQueryState>) {
    this.isCaseTypeDisabled = false;
    this.stages = ['Pre-Arrangement', 'Arrangement', 'Filing'];
    this.inputFormErrors = {
      copyCount: {}
    };
  }

  ngOnInit () {
    this.isReadOnly = this.data.input.isReadOnly;
    this.isArchived = (this.data.input.isArcived == 'Yes')?true : false;

    this.inputForm = this.formBuilder.group({
      copyCount: [this.isReadOnly ? 1: 10, Validators.compose([Validators.required, Validators.min(1), Validators.max(100)])],
      selectedFiledPeriod: [],
      specifyField: [],
      archiveExisting: this.isArchived,
      caseIdUsedforCorrection: false,
      retainCaseId: false
    });

    this.isCaseTypeDisabled = this.data.input.disableTypeChange;
    this.selectedStage = this.data.input.caseType;
    this.getCaseContentLinkItems();
    this.getFilingModels();
    this.selectedStageId = this.stages.indexOf(this.selectedStage);
    this.inputForm.valueChanges.subscribe(() => {
      this.onFormValuesChanged();
    });
  }

  onOkClicked() {
    this.data.input['caseType'] = this.selectedStage;
    this.data.input['copyCount'] = this.inputForm.controls['copyCount'].value;
    if (this.selectedStage === 'Pre-Arrangement') {
      this.data.input['selectedCase'] = this.caseContentLinkItems.filter(o => o.linkItemType === Configuration.getBasketMappedSourceType(CaseType.PreArrangement))[0].linkItemId;
    } else if (this.selectedStage === 'Arrangement') {
      this.data.input['selectedCase'] = this.inputForm.controls['selectedFiledPeriod'].value.linkItemId;
    } else if (this.selectedStage === 'Filing') {
      this.data.input['selectedCase'] = this.inputForm.controls['selectedFiledPeriod'].value.id;
    }
    this.data.input['specifyField'] = this.inputForm.controls['specifyField'].value;
    this.data.input['archiveExisting'] = this.inputForm.controls['archiveExisting'].value;
    this.data.input['caseIdUsedforCorrection'] = this.inputForm.controls['caseIdUsedforCorrection'].value;
    return true;
  }

  onFormValuesChanged() {
    for (const field in this.inputFormErrors) {
      if (!this.inputFormErrors.hasOwnProperty(field)) {
        continue;
      }
      this.inputFormErrors[field] = {};
      const control = this.inputForm.get(field);
      if (control && control.dirty && !control.valid) {
        this.inputFormErrors[field] = control.errors;
      }
    }
    this.data.isDisabledOkButton = !this.inputForm.valid;
  }

  private getCaseContentLinkItems() {
    const queryKey = `DAC6AllCaseContentLinkItemByCaseId`;
    QueryProvider.invokeQuery(this.queryStore, queryKey, {'[caseId]': this.data.input.caseId});
    this.particleStore.pipe(select(getQueryResponse, queryKey),skip(1), takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      this.caseContentLinkItems = res.items;
      this.onStageChanged();
    });
  }

  onStageChanged() {
    if (this.selectedStage === 'Arrangement') {
      const filedPeriodList = this.caseContentLinkItems.filter(o => o.linkItemType === BasketProvider.getBasketMappedSourceType(this.selectedStage));
      this.filedPeriodList$.next(filedPeriodList);
      this.inputForm.controls['selectedFiledPeriod'].setValue(filedPeriodList[0]);
      if (this.data.input.filedPeriodTitle) {
        const filedPeriod = filedPeriodList.filter(o => o.filedPeriodTitle === this.data.input.filedPeriodTitle);
        this.inputForm.controls['selectedFiledPeriod'].setValue(filedPeriod[0]);
      }
    } else if (this.selectedStage === 'Filing') {
      const filedPeriodList = this.filingModels;
      this.filedPeriodList$.next(filedPeriodList);
      this.inputForm.controls['selectedFiledPeriod'].setValue(filedPeriodList[0]);
      if (this.data.input.filedPeriodTitle) {
        const filedPeriod = filedPeriodList.filter(o => o.filedPeriodTitle === this.data.input.filedPeriodTitle);
        this.inputForm.controls['selectedFiledPeriod'].setValue(filedPeriod[0]);
      }
    }
    this.availableStages = this.caseContentLinkItems.map(o => o.linkItemType);
    this.availableStages.push(this.data.input.caseType);
  }

  private getFilingModels() {
    if (this.selectedStage === 'Filing') {
      const queryKey = `DAC6AllFilingModelByCaseId`;
      QueryProvider.invokeQuery(this.queryStore, queryKey, {'[caseId]': this.data.input.caseId});
      this.particleStore.pipe(select(getQueryResponse, queryKey),skip(1), takeUntil(this.unsubscribe$)).subscribe(res => {
        this.filingModels = res.items;
        this.onStageChanged();
      });
    }
  }
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

